import { useCallback } from 'react'
import { Button, Form, Input, Tag, Typography } from 'antd'
import { Plus, Trash2 } from 'lucide-react'
import { Authorized } from '@/features/authorization/authorized.tsx'
import { AttachmentUpload } from '@/features/files/index.ts'
import { EnterpriseSelector, type EnterpriseSelection } from '@/features/service-enterprise/index.ts'
import { cn } from '@/utils/index.ts'
import { QuickBusinessSelect } from './quick-business-select.tsx'
import { RentalContractFields } from './rental-contract-fields.tsx'
import { ServiceOrderCreatePricingFields } from './service-order-create-pricing-fields.tsx'
import type { BusinessOption, ServiceOrderItemFormValues } from './service-order-form.types.ts'

export function ServiceOrderCreateItemsFields({
  customerId,
  inheritEnterprise = false,
  optional = false,
  embedded = false,
  businessLoading,
  canCreateBusiness = false,
  businessOptions,
  disabled,
  onEnterprisePendingChange,
  onBusinessChange,
  onBusinessSearch
}: {
  customerId?: number
  inheritEnterprise?: boolean
  optional?: boolean
  embedded?: boolean
  canCreateBusiness?: boolean
  businessLoading: boolean
  businessOptions: BusinessOption[]
  disabled: boolean
  onEnterprisePendingChange?: (key: number, pending: boolean) => void
  onBusinessChange: (fieldName: number, value: number, option?: BusinessOption) => void
  onBusinessSearch: (value: string) => void
}) {
  return (
    <section
      className={cn(!embedded && 'border-border bg-surface scroll-mt-24 rounded-xl border p-5 shadow-xs')}
      id={embedded ? undefined : 'section-service-items'}
    >
      {!embedded && (
        <div className="mb-5 flex items-start justify-between gap-3">
          <div>
            <Typography.Title className="mb-1!" level={5}>
              服务业务
            </Typography.Title>
            <Typography.Text type="secondary">
              选择业务后自动带入服务模式和参考价格，每项业务生成一条独立服务订单。
            </Typography.Text>
          </div>
          <Tag color="blue">可添加多个</Tag>
        </div>
      )}

      <Form.List
        name="items"
        rules={[
          {
            validator: async (_, value: ServiceOrderItemFormValues[] | undefined) => {
              if (!optional && !value?.length) {
                throw new Error('至少添加一个服务业务')
              }
            }
          }
        ]}
      >
        {(fields, { add, remove }, { errors }) => (
          <div className="flex flex-col gap-4">
            {fields.map(({ key, name, ...restField }, index) => (
              <div
                aria-label={`服务项目 ${index + 1}`}
                className="border-border bg-surface-muted/35 rounded-lg border p-4"
                key={key}
                role="group"
              >
                <div className="mb-4 flex items-center justify-between gap-3">
                  <Typography.Text strong>服务项目 {index + 1}</Typography.Text>
                  <Authorized permissions={['order.service.delete']}>
                    <Button
                      aria-label={`删除服务项目 ${index + 1}`}
                      disabled={disabled || (!optional && fields.length <= 1)}
                      icon={<Trash2 aria-hidden size={15} />}
                      onClick={() => remove(name)}
                      size="small"
                      type="text"
                      danger
                    >
                      删除
                    </Button>
                  </Authorized>
                </div>
                <div className="grid grid-cols-1 gap-x-6 lg:grid-cols-2">
                  <Form.Item
                    {...restField}
                    label="服务业务"
                    name={[name, 'businessId']}
                    rules={[{ message: '请选择服务业务', required: true }]}
                  >
                    <QuickBusinessSelect
                      businessType="valueAdded"
                      canCreate={canCreateBusiness}
                      disabled={disabled}
                      loading={businessLoading}
                      onChange={(value, option) => onBusinessChange(name, value, option)}
                      options={businessOptions}
                      placeholder="请选择增值业务"
                      showSearch={{ onSearch: onBusinessSearch, filterOption: false }}
                    />
                  </Form.Item>
                  <div>
                    {inheritEnterprise ? (
                      <Typography.Paragraph type="secondary">
                        服务对象继承本次租赁的选择，随租赁订单统一保存。
                      </Typography.Paragraph>
                    ) : (
                      <Form.Item {...restField} label="服务对象（选填）" name={[name, 'enterpriseSelection']}>
                        <ServiceItemEnterpriseSelector
                          key={customerId}
                          itemKey={key}
                          customerId={customerId}
                          disabled={disabled}
                          onPendingChange={onEnterprisePendingChange}
                        />
                      </Form.Item>
                    )}
                  </div>
                </div>
                <ServiceOrderCreatePricingFields name={name} disabled={disabled} />
                <Form.Item {...restField} label="附件" name={[name, 'fileIds']}>
                  <AttachmentUpload purpose="serviceAttachment" disabled={disabled} maxCount={20} />
                </Form.Item>
                <Form.Item {...restField} label="备注" name={[name, 'note']}>
                  <Input.TextArea
                    disabled={disabled}
                    maxLength={5000}
                    placeholder="补充本项服务说明（选填）"
                    rows={3}
                    showCount
                  />
                </Form.Item>
                <RentalContractFields service itemName={name} />
              </div>
            ))}
            <Form.ErrorList errors={errors} />
            <Button
              block
              disabled={disabled}
              icon={<Plus aria-hidden size={16} />}
              onClick={() => add({ fileIds: [], note: '' })}
              type="dashed"
            >
              添加服务业务
            </Button>
          </div>
        )}
      </Form.List>
    </section>
  )
}

function ServiceItemEnterpriseSelector({
  itemKey,
  onPendingChange,
  ...props
}: {
  itemKey: number
  customerId?: number
  disabled: boolean
  id?: string
  value?: EnterpriseSelection
  onChange?: (value: EnterpriseSelection) => void
  onPendingChange?: (key: number, pending: boolean) => void
}) {
  const reportPending = useCallback(
    (pending: boolean) => onPendingChange?.(itemKey, pending),
    [itemKey, onPendingChange]
  )
  return <EnterpriseSelector purpose="order.service.read" {...props} onPendingChange={reportPending} />
}
